import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Json } from '@/integrations/supabase/types';

type JsonRecord = Record<string, Json | undefined>;

export interface InstagramProfilePlannerItem {
  id: string;
  planId: string;
  sourceMediaId: string | null;
  targetId: string | null;
  position: number;
  mediaUrl: string | null;
  coverImageUrl: string | null;
  mediaType: string | null;
  fileName: string | null;
  missingSource: boolean;
}

export interface InstagramProfilePlannerSnapshot {
  planId: string | null;
  connectionId: string;
  version: number;
  updatedAt: string | null;
  items: InstagramProfilePlannerItem[];
}

export interface PlannerMutationResult {
  planId: string;
  version: number;
}

export interface CreatePlannerPostsOutcome {
  itemId: string;
  ok: boolean;
  postId?: string;
  targetId?: string;
  code?: string;
  problem?: string;
}

export interface CreatePlannerPostsResult extends PlannerMutationResult {
  outcomes: CreatePlannerPostsOutcome[];
}

export interface InstagramPlannerTarget {
  id: string;
  postId: string;
  connectionId: string;
  platform: string;
  placement: string;
  nativeOptions: Json;
  approvalStatus: string;
  deliveryStatus: string;
  scheduledAt: string | null;
}

function recordFrom(value: Json): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
}

function stringFrom(record: JsonRecord, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

class InstagramProfilePlannerService extends EnhancedBaseService {
  private static instance: InstagramProfilePlannerService;

  static getInstance(): InstagramProfilePlannerService {
    if (!InstagramProfilePlannerService.instance) {
      InstagramProfilePlannerService.instance = new InstagramProfilePlannerService({
        deduplication: { enabled: true, ttl: 2_000 },
      });
    }
    return InstagramProfilePlannerService.instance;
  }

  async getPlan(connectionId: string): Promise<ServiceResponse<InstagramProfilePlannerSnapshot>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data: plan, error: planError } = await this.client
        .from('social_profile_plans')
        .select('id, connection_id, version, updated_at')
        .eq('connection_id', connectionId)
        .maybeSingle();

      if (planError) throw new Error(planError.message);
      if (!plan) {
        return {
          planId: null,
          connectionId,
          version: 0,
          updatedAt: null,
          items: [],
        };
      }

      const { data: rows, error: itemsError } = await this.client
        .from('social_profile_plan_items')
        .select(
          'id, profile_plan_id, source_media_id, target_id, position, source_snapshot, updated_at',
        )
        .eq('profile_plan_id', plan.id)
        .order('position', { ascending: true })
        .order('id', { ascending: true });

      if (itemsError) throw new Error(itemsError.message);

      const items = (rows ?? []).map((row): InstagramProfilePlannerItem => {
        const source = recordFrom(row.source_snapshot);
        const storageBucket = stringFrom(source, 'storageBucket') ?? 'post_media';
        const resolve = (value: string | null): string | null => {
          if (!value || /^(https?:|data:)/i.test(value)) return value;
          return this.client.storage.from(storageBucket).getPublicUrl(value).data.publicUrl;
        };
        const mediaUrl = resolve(stringFrom(source, 'url'));
        const coverImageUrl = resolve(stringFrom(source, 'coverImageUrl'));
        return {
          id: row.id,
          planId: row.profile_plan_id,
          sourceMediaId: row.source_media_id,
          targetId: row.target_id,
          position: row.position,
          mediaUrl,
          coverImageUrl,
          mediaType: stringFrom(source, 'mediaType'),
          fileName: stringFrom(source, 'fileName'),
          missingSource: !row.source_media_id && !row.target_id,
        };
      });

      return {
        planId: plan.id,
        connectionId: plan.connection_id,
        version: plan.version,
        updatedAt: plan.updated_at,
        items,
      };
    }, 'Failed to load the Instagram profile plan');
  }

  async getTarget(targetId: string): Promise<ServiceResponse<InstagramPlannerTarget | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client
        .from('social_post_targets')
        .select(
          'id, post_id, connection_id, platform, placement, native_options_snapshot, approval_status, delivery_status, scheduled_at',
        )
        .eq('id', targetId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      return {
        id: data.id,
        postId: data.post_id,
        connectionId: data.connection_id,
        platform: data.platform,
        placement: data.placement,
        nativeOptions: data.native_options_snapshot,
        approvalStatus: data.approval_status,
        deliveryStatus: data.delivery_status,
        scheduledAt: data.scheduled_at,
      };
    }, 'Failed to load the exact Instagram target');
  }

  async addCandidates(input: {
    connectionId: string;
    mediaIds: string[];
    expectedVersion: number | null;
    projectId?: string | null;
  }): Promise<ServiceResponse<PlannerMutationResult & { insertedItemIds: string[] }>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client.rpc('add_instagram_profile_plan_candidates', {
        p_connection_id: input.connectionId,
        p_media_ids: input.mediaIds,
        p_expected_version: input.expectedVersion ?? undefined,
        p_project_id: input.projectId ?? undefined,
      });
      if (error) throw new Error(error.message);
      const result = recordFrom(data);
      return {
        planId: stringFrom(result, 'planId') ?? '',
        version: Number(result.version ?? 0),
        insertedItemIds: Array.isArray(result.insertedItemIds)
          ? result.insertedItemIds.filter((value): value is string => typeof value === 'string')
          : [],
      };
    }, 'Failed to add media to the Instagram plan');
  }

  async savePositions(input: {
    planId: string;
    expectedVersion: number;
    orderedItemIds: string[];
  }): Promise<ServiceResponse<PlannerMutationResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client.rpc('save_instagram_profile_plan_positions', {
        p_plan_id: input.planId,
        p_expected_version: input.expectedVersion,
        p_ordered_item_ids: input.orderedItemIds,
      });
      if (error) throw new Error(error.message);
      return { planId: input.planId, version: data };
    }, 'Failed to save the Instagram plan order');
  }

  async removeCandidate(input: {
    planId: string;
    itemId: string;
    expectedVersion: number;
  }): Promise<ServiceResponse<PlannerMutationResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client.rpc('remove_instagram_profile_plan_candidate', {
        p_plan_id: input.planId,
        p_item_id: input.itemId,
        p_expected_version: input.expectedVersion,
      });
      if (error) throw new Error(error.message);
      return { planId: input.planId, version: data };
    }, 'Failed to remove the Instagram plan candidate');
  }

  async createPosts(input: {
    planId: string;
    itemIds: string[];
    expectedVersion: number;
    operationId: string;
  }): Promise<ServiceResponse<CreatePlannerPostsResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticatedOwner();
      const { data, error } = await this.client.rpc('create_instagram_posts_from_plan_items', {
        p_plan_id: input.planId,
        p_item_ids: input.itemIds,
        p_expected_version: input.expectedVersion,
        p_operation_id: input.operationId,
      });
      if (error) throw new Error(error.message);
      const result = recordFrom(data);
      const outcomes = Array.isArray(result.outcomes)
        ? result.outcomes.map((value): CreatePlannerPostsOutcome => {
            const outcome = recordFrom(value);
            return {
              itemId: stringFrom(outcome, 'itemId') ?? '',
              ok: outcome.ok === true,
              postId: stringFrom(outcome, 'postId') ?? undefined,
              targetId: stringFrom(outcome, 'targetId') ?? undefined,
              code: stringFrom(outcome, 'code') ?? undefined,
              problem: stringFrom(outcome, 'problem') ?? undefined,
            };
          })
        : [];
      return {
        planId: stringFrom(result, 'planId') ?? input.planId,
        version: Number(result.version ?? input.expectedVersion),
        outcomes,
      };
    }, 'Failed to create Instagram posts from the selected media');
  }
}

export const instagramProfilePlannerService = InstagramProfilePlannerService.getInstance();
export { InstagramProfilePlannerService };
